import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1065 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 7004 Extension Guide for Partnership Form 1065"
          description="Explore our comprehensive guide to filing a Form 7004 extension for Form 1065. Learn about deadlines, requirements, & how to extend your tax filing period."
          keywords=""
          canonical="resources/tax-extension/form-7004-for-form-1065/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">
              Understanding Partnership Tax Filing:
            </span>
          </span>
          <br />A Guide to Form 1065
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-7004-for-form-1065/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7004 for 1065 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>Form 1065</span> is
                        used by partnerships to report the business's income,
                        deductions, gains, losses, and each partner's share of
                        taxable income to the IRS.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>Partnerships</span>{' '}
                        must{' '}
                        <a
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1065-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 1065
                        </a>{' '}
                        by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          15th day of the third month
                        </span>{' '}
                        after the end of the tax year. If additional time is
                        needed,{' '}
                        <span style={{ fontWeight: 600 }}>Form 7004</span> can
                        be filed to request an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic extension of up to 6 months
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Late filing penalties
                        </span>{' '}
                        for Form 1065 are generally{' '}
                        <span style={{ fontWeight: 600 }}>
                          charged per partner, per month
                        </span>
                        , for up to 12 months. Additional penalties may apply
                        for failing to furnish or correctly report{' '}
                        <span style={{ fontWeight: 600 }}>Schedule K-1</span>{' '}
                        information, with higher penalties for intentional
                        disregard.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>Form 7004</span>{' '}
                        extends the{' '}
                        <span style={{ fontWeight: 600 }}>time to file</span>{' '}
                        Form 1065 but{' '}
                        <span style={{ fontWeight: 600 }}>
                          does not extend the time to pay
                        </span>{' '}
                        any tax due. Estimated taxes should be paid by the
                        original due date to avoid penalties and interest.
                      </span>
                    </li>
                  </Box>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Introduction to Form 7004
                </Typography> */}

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Partnerships are a common business form in which two or more
                  people share ownership and responsibility. Partnerships must
                  comply with certain tax regulations, which include accurately
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1065
                  </Link>
                  . The foundations of partnership taxation are examined below,
                  along with the significance of Form 1065 for maintaining
                  compliance.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 1065?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/business-tax/form-1065-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1065
                  </Link>
                  , also known as the &quot;U.S. Return of Partnership
                  Income,&quot; is a tax form used by partnerships to submit
                  their financial information to the IRS. Unlike corporations,
                  partnerships do not pay taxes directly; instead, revenues are
                  distributed to individual partners, who report them on their
                  personal tax returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 1065 outlines income, deductions, and distributions to
                  partners.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Key components and requirements of Form 1065
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Key components:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Partnership information: </b> Partnership information
                      includes your company&apos;s name, EIN, and principal
                      place of operation.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Income and expenses: </b> Report your business&apos;s
                      revenue from multiple sources, such as sales, interest,
                      and rent. Don&apos;t forget to disclose all of your
                      authorized expenses, including advertising and salary.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Deductions and credits: </b> Eligible deductions
                      include depreciation and business travel expenditures.
                      Credits, such as the R&D credit, can potentially lower
                      your tax liability.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Partner information: </b> List each partner&apos;s
                      ownership portion, tax identification number, and address.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Schedules: </b> Attach extra forms that give additional
                      information, such as <b>Schedule K-1</b> for partner
                      allocations and <b>Schedule M-1</b> for income
                      reconciliation.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Requirements:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  <ul style={{ marginBottom: 24 }}>
                    <li style={{ marginBottom: 16 }}>
                      <b>Accuracy: </b>All information on Form 1065 must be
                      accurate and accompanied by documentation.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Timeline: </b>Form 1065 must be filed by the due date,
                      which is typically the 15th day of the third month
                      following the conclusion of the partnership&apos;s tax
                      year.{' '}
                      <Link
                        href="/resources/tax-extension/form-7004-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 7004
                      </Link>{' '}
                      can be used to request Form 1065 extension if more time is
                      required.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Compliance: </b>When filling out Form 1065,
                      partnerships must follow IRS regulations and procedures,
                      including accurately reporting all income and deductions.
                    </li>
                  </ul>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Understanding these key components and requirements is
                  essential for partnerships to complete and file Form 1065
                  correctly and in accordance with IRS regulations.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the deadline to file Form 1065?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 1065 must be filed by the 15th of the third month after
                  the end of a company&apos;s tax year. So, if the partnership
                  follows the calendar year, Form 1065 must be filed by{' '}
                  <b>March 16</b>. If the company requires an extension, it must
                  file Form 7004 by the appropriate deadline for the tax year.
                  When filed by the deadline, the corporation is automatically
                  granted a six-month extension.
                </Typography>
                {/* <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS will not contact the firm if the extension is
                  approved, but it will if rejected. Each partner must{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 7004
                  </Link>{' '}
                  unless the company has a common parent or agency, in which
                  case a blanket request can be made.
                </Typography> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What Is the Penalty for Failing to File Form 1065?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The penalty for failing to file Form 1065 or filing it late is
                  generally $255 for each month or part of a month the return is
                  late, multiplied by the number of partners in the partnership
                  for any portion of the partnership&apos;s tax year for which
                  the return is due. This penalty may accrue for up to 12
                  months.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, a penalty of $340 may be imposed for each failure to
                  provide Schedule K-1 (and K-3, if applicable) to a partner on
                  time, or for each failure to include all required information
                  (or incorrect information) on Schedule K-1 (and K-3, if
                  applicable). The maximum yearly penalty for entities with
                  gross receipts greater than $5,000,000 is $4,098,500 while
                  those with gross receipts equal to or less than $5,000,000
                  face a $1,366,000 fine. If correct information reporting is
                  intentionally disregarded the penalty per instance increases
                  to $680 or 10% of the total number of items required to be
                  reported, whichever is greater. There is no maximum penalty
                  for intentional disregard.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Tips for a smooth filing experience
                </Typography>

                <Typography component="div" sx={{ mb: 0 }}>
                  <ul style={{ marginBottom: 24 }}>
                    <li style={{ marginBottom: 16 }}>
                      {' '}
                      Gather all your financial documents well in advance.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      File electronically for faster processing and
                      error-checking.
                    </li>

                    <li style={{ marginBottom: 16 }}>
                      Double-check your entries and calculations before
                      submitting.
                    </li>
                  </ul>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, what happens if you&apos;re not quite ready and the
                  deadline approaches? Do not be afraid! You can use Form 7004
                  to request an extension.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 7004?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7004, Application for Automatic Extension of Time To File
                  Certain Business Income Tax, Information, and Other Returns,
                  is filed by businesses in the United States to get an
                  extension for filing certain business income taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  By filing{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 7004
                  </Link>
                  , businesses can get up to 6 months of extension. Also,
                  businesses don&apos;t need to provide a reason for filing form
                  7004 extension.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can E-file Extension Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Most businesses that want extra time to file their business
                  income tax return can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 7004
                  </Link>
                  . Below business types can use Form 7004 extension to file
                  business income tax:
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>C-corporations (Form 1120)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      S-corporations (
                      <Link
                        href="/resources/business-tax/form-1120-s-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1120S
                      </Link>
                      )
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Partnerships (Form 1065)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Multiple-member LLCs as corporation and partnership
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Trust, Estates and Others (Form 1041)
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The IRS recommends electronic filing over paper
                  filing for its quick and instant status returns.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 7004 electronically?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can file your Form 7004 electronically with TaxZerone in
                  just 3 simple steps.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1 : </b>Choose the tax year and enter the information
                  requested.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2 : </b>Determine the sum payable by providing an
                  estimate of total tax, payments, and credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>Step 3 : </b>Send the return to the IRS.
                </Typography>
                {/* <Box
                  component={'div'}
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Do you have the information required, and do you find the
                      steps simple?
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 7004 with TaxZerone and get an extension of up
                      to 6 months.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1065`}
                    >
                      E-file Now
                    </Button>
                  </Box>
                </Box> */}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 7004 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your Form 7004 easily with TaxZerone at just{' '}
              <b>$17.99/return</b>. You will only have to enter the required
              information, calculate your balance due, and transmit the return
              to the IRS.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You will also get updates on your return status as soon as the IRS
              processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 7004 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1065`}
              >
                E-file Form 7004 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Takes 3 steps and less than 5 minutes
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1065;
